import {
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  Spinner,
  useToast,
  Flex,
  Select,
  Badge,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../axios-client';
import { useStateContext } from '../../../context/ContextProvider';
import MyPagination from '../../../components/MyPagination';
import { motion, AnimatePresence } from 'framer-motion';
import CountdownTimer from './CountdownTimer';

function ListSubscriptionsPlans() {
  const { user, setUser, setToken, dataSubscriptionUser } = useStateContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [paginationData, setPaginationData] = useState()
  const [loading, setLoading] = useState(true);
  const bgBox = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();

  //search variable
  const [searchTerm, setSearchTerm] = useState('')
  // PAGINATION

  const [currentPage, setCurrentPage] = useState(0);

  ////////////////////////////////the size of the table//////////////////////////////
  const PAGE_SIZE = 10;
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const fetchSubscriptionPlans = async (signal) => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/subscription-plans/getAllPlans/?page_size=${pageSize}&page=${currentPage + 1}&search_term=${searchTerm}&client_id=${user?.id}`, { signal });
      if (response.data.results.length !== 0) {
        setPlans(response.data.results);
        setPaginationData(response.data)
      }
      else {
        setPlans([]);
        setPaginationData({})
      }

      if (response.data.error === 'Client is disabled.' && response.data.etat === false) {
        setUser({});
        setToken(null);
        localStorage.removeItem("tokenExpirationTime");
        localStorage.removeItem("refreshTimeAdmin");
        localStorage.removeItem("role");

        // Vérifier si le toast n'est pas déjà affiché
        if (!toast.isActive('accountDisabled')) {
          toast({
            id: 'accountDisabled',
            description: t("le compte est désactivé"),
            status: 'warning',
            variant: 'left-accent',
            duration: 2000,
            isClosable: true,
            position: 'bottom-right'
          });
        }
      }
      setLoading(false);
    } catch (error) {
      if (error.name !== 'CanceledError' && error.message !== 'canceled') {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (user?.id) {
      const abortController = new AbortController();
      fetchSubscriptionPlans(abortController.signal);
      return () => abortController.abort();
    }
  }, [user?.id, currentPage, pageSize, searchTerm]);

  if (loading) {
    return (
      <Container maxW="6xl" py={10} centerContent>
        <Spinner size="xl" />
      </Container>
    );
  }

  // Check if user has a pending subscription request
  const hasPendingRequest = dataSubscriptionUser?.has_subscription && dataSubscriptionUser?.subscription?.demande;

  // Check if user has an inactive subscription
  const hasInactiveSubscription = dataSubscriptionUser?.has_subscription &&
    dataSubscriptionUser?.subscription &&
    !dataSubscriptionUser?.subscription?.is_active &&
    !dataSubscriptionUser?.subscription?.demande;

  return (
    <Container maxW="6xl" py={10}>
      {hasPendingRequest && (
        <Alert status="info" mb={8} borderRadius="md">
          <AlertIcon />
          {t("Votre demande d'abonnement pour le plan")} <strong> {dataSubscriptionUser?.subscription?.plan_name} </strong> {t("est en attente d'approbation")}
        </Alert>
      )}

      {hasInactiveSubscription && (
        <Alert status="error" mb={8} borderRadius="md">
          <AlertIcon />
          {t('Votre abonnement au plan ')} <strong> {dataSubscriptionUser?.subscription?.plan_name} </strong> {t(' a été désactivé')}
        </Alert>
      )}

      <Stack spacing={6} textAlign="center" mb={10}>
        <Heading fontSize={{ base: '2xl', md: '4xl' }}>
          {t("Choisissez votre forfait d'abonnement")}
        </Heading>
        <Text color="gray.500">
          {t('Trouvez le forfait qui correspond à vos besoins.')}
        </Text>
      </Stack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {plans.map((plan) => {

          // Calculate days remaining on subscription
          let daysRemaining
          if (dataSubscriptionUser?.subscription?.plan === plan.id) {
            const endDate = new Date(dataSubscriptionUser.subscription.end_date);
            const currentDate = new Date(plan.current_time)
            const timeDiff = endDate - currentDate;
            daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
          }

          // Check if this plan is the user's current subscription
          const isCurrentPlan = dataSubscriptionUser?.has_subscription &&
            dataSubscriptionUser?.subscription?.plan === plan.id;

          // Check if this is the pending plan
          const isPendingPlan = isCurrentPlan && dataSubscriptionUser?.subscription?.demande;

          // Check if this is the active plan
          const isActivePlan = isCurrentPlan &&
            !dataSubscriptionUser?.subscription?.demande &&
            dataSubscriptionUser?.subscription?.is_active;

          // Check if this is the inactive plan
          const isInactivePlan = isCurrentPlan &&
            !dataSubscriptionUser?.subscription?.demande &&
            !dataSubscriptionUser?.subscription?.is_active;

          return (
            <Box
              key={plan.id}
              borderWidth="1px"
              borderRadius="2xl"
              bg={bgBox}
              p={6}
              shadow="md"
              borderColor={
                isPendingPlan ? "orange.300" :
                  isActivePlan ? "teal.300" :
                    isInactivePlan ? "red.300" :
                      borderColor
              }
              position="relative"
            >
              {isPendingPlan && (
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme="orange"
                  fontSize="sm"
                  borderRadius="md"
                  px={2}
                >
                  {t("En attente d'approbation")}
                </Badge>
              )}
              {isActivePlan && (
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme="teal"
                  fontSize="sm"
                  borderRadius="md"
                  px={2}
                >
                  {t('Plan actuel')}
                </Badge>
              )}
              {isInactivePlan && (
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme="red"
                  fontSize="sm"
                  borderRadius="md"
                  px={2}
                >
                  {t('Désactivé')}
                </Badge>
              )}
              <Heading fontSize="xl" mb={3}>
                {plan.name}
              </Heading>
              <Flex>
                <Text fontSize="2xl" fontWeight="bold" color="teal.500">
                  {plan.price}
                </Text>
                <Text fontSize={18} ml={2} fontWeight="bold" color="teal.500">{t("MAD")}</Text>
                <Text fontSize="2xl" ml={2} fontWeight="bold" color="teal.500">
                  / {plan.billing_cycle}
                </Text>
              </Flex>


              <Stack spacing={1} mt={4} fontSize="sm" color="gray.600">
                <Text>👤 {t('Accounts')}: {plan.max_accounts === 0 ? t('Illimité') : plan.max_accounts}</Text>
                <Text>🛒 {t('Commandes')}: {plan.max_orders === 0 ? t('Illimité') : plan.max_orders}</Text>
                <Text>🛍️ {t('Plateformes de vente')}: {plan.max_store_platforms === 0 ? t('Illimité') : plan.max_store_platforms}</Text>
                <Text>🚚 {t('Transporteurs')}: {plan.max_shipping_companies === 0 ? t('Illimité') : plan.max_shipping_companies}</Text>
                <Text>📦 {t('Produits')}: {plan.max_products === 0 ? t('Illimité') : plan.max_products}</Text>
                <Text>🏢 {t('Businesses')}: {plan.max_businesses === 0 ? t('Illimité') : plan.max_businesses}</Text>
              </Stack>

              {isPendingPlan ? (
                <Box mt={6} borderWidth="1px" borderRadius="md" p={3} bg="orange.50">
                  <Stack spacing={1}>
                    <Text fontWeight="medium" color="orange.700">
                      {t("Demande d'abonnement en attente")}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t("Votre demande est en cours de traitement")}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {t('Demandé le')}: {new Date(dataSubscriptionUser?.subscription?.date_created).toLocaleDateString()}
                    </Text>
                  </Stack>
                </Box>
              ) : isActivePlan ? (
                <CountdownTimer endDate={dataSubscriptionUser?.subscription?.end_date} currentTime={dataSubscriptionUser?.subscription?.current_time} />

              ) : isInactivePlan ? (
                <Box mt={6} borderWidth="1px" borderRadius="md" p={3} bg="red.50">
                  <Stack spacing={1}>
                    <Text fontWeight="medium" color="red.700">
                      {t('Abonnement désactivé')}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('Veuillez choisir un nouveau forfait ou contacter le support')}
                    </Text>
                    {plan.visible && <Button
                      mt={2}
                      colorScheme="purple"
                      width="full"
                      isDisabled={hasPendingRequest}
                      onClick={() => navigate(`/paiement`, {
                        state: {
                          planId: plan.id,
                          planName: plan.name,
                          planPrice: plan.price,
                          billingCycle: plan.billing_cycle,
                        },
                      })}
                    >
                      {t("S'abonner")}
                    </Button>}
                  </Stack>
                </Box>
              ) : (
                plan.visible && <Button
                  mt={6}
                  colorScheme="purple"
                  width="full"
                  isDisabled={hasPendingRequest}
                  onClick={() => navigate(`/paiement`, {
                    state: {
                      planId: plan.id,
                      planName: plan.name,
                      planPrice: plan.price,
                      billingCycle: plan.billing_cycle,
                    },
                  })}
                >
                  {t("S'abonner")}
                </Button>
              )}
            </Box>
          );
        })}
      </SimpleGrid>

      {/* //////////////////////////////////////////Pagination////////////////////////////////////////// */}
      <Flex flexDirection={{ base: 'column', lg: "row" }} justify="space-between" align="center" w="100%" mt={8}>
        <Box flex="1">
          <MyPagination
            paginationData={paginationData}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            PAGE_SIZE={pageSize}
          />
        </Box>

        <Select rounded={"md"} size={"sm"} w="70px" value={pageSize} onChange={handlePageSizeChange}>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </Select>
      </Flex>
    </Container>
  );
}

export default ListSubscriptionsPlans;